import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { TenantInviteReadyModal } from '../../components/TenantInviteReadyModal';
import {
  Badge,
  Button,
  Callout,
  CheckCircleIcon,
  ClockIcon,
  DateInput,
  ExternalLinkIcon,
  FolderIcon,
  Label,
  ListEmptyState,
  LoadingSpinner,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  PencilIcon,
  PlusIcon,
  RefreshIcon,
  SaveIcon,
  SearchIcon,
  Switch,
  TextInput,
  TrashIcon,
  UsersIcon,
  WarningIcon,
  XIcon,
  useConfirm,
} from '../../components/ui';
import { t } from '../../i18n';
import { useAuth } from '../../hooks/useAuth';
import {
  ApiError,
  api,
  type AppPackage,
  type PackageStatus,
  type Tenant,
  type TenantPackageRenewal,
} from '../../services/api';

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function toDateOnly(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

function formatDateGr(value: string | null | undefined): string {
  const day = toDateOnly(value);
  if (!day) return '—';
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
}

function defaultPackageDates(): { start: string; end: string } {
  const start = new Date();
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  end.setDate(end.getDate() - 1);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: fmt(start), end: fmt(end) };
}

function packageBadgeVariant(
  status: PackageStatus | undefined,
): 'success' | 'warning' | 'danger' | 'secondary' {
  if (status === 'active') return 'success';
  if (status === 'upcoming') return 'warning';
  if (status === 'expired') return 'danger';
  return 'secondary';
}

function packageStatusLabel(
  status: PackageStatus | undefined,
  labels: ReturnType<typeof t>['platform'],
): string {
  if (status === 'active') return labels.packageStatusActive;
  if (status === 'upcoming') return labels.packageStatusUpcoming;
  if (status === 'expired') return labels.packageStatusExpired;
  return labels.packageStatusDisabled;
}

function renewalKindLabel(
  kind: string,
  labels: ReturnType<typeof t>['platform'],
): string {
  if (kind === 'initial') return labels.renewalKindInitial;
  if (kind === 'manual') return labels.renewalKindManual;
  if (kind === 'cancelled') return labels.renewalKindCancelled;
  return labels.renewalKindRenewal;
}

/** Active = current or future (not expired). */
function isActivePackageRow(period: {
  startsAt: string;
  endsAt: string;
}): boolean {
  const today = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const end = toDateOnly(period.endsAt);
  return Boolean(end && end >= today);
}

function StatCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}) {
  const toneClass =
    tone === 'success'
      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
      : tone === 'warning'
        ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
        : 'bg-teal-500/10 text-teal-700 dark:text-teal-300';

  return (
    <div className="rounded-xl border border-line bg-panel p-4 shadow-bf-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
          </p>
          <p className="mt-2 font-display text-3xl font-semibold tabular-nums text-ink">
            {value}
          </p>
        </div>
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${toneClass}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

export function TenantsPage() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { establishSession } = useAuth();
  const labels = t().platform;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const defaults = defaultPackageDates();
  const [packageStartsAt, setPackageStartsAt] = useState(defaults.start);
  const [packageEndsAt, setPackageEndsAt] = useState(defaults.end);
  const [editPackageTenant, setEditPackageTenant] = useState<Tenant | null>(
    null,
  );
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [packageSaving, setPackageSaving] = useState(false);
  const [renewingId, setRenewingId] = useState<number | null>(null);
  const [deletingRenewalId, setDeletingRenewalId] = useState<number | null>(
    null,
  );
  const [editingRenewalId, setEditingRenewalId] = useState<number | null>(null);
  const [historyRows, setHistoryRows] = useState<TenantPackageRenewal[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [inviteResult, setInviteResult] = useState<{
    tenantId: number;
    inviteUrl: string;
    email: string;
    tenantName: string;
    recipientName: string;
    inviteEmail?: {
      subject: string;
      plainText: string;
      html: string;
    };
  } | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [enteringId, setEnteringId] = useState<number | null>(null);
  const [appPackagesTenant, setAppPackagesTenant] = useState<Tenant | null>(
    null,
  );
  const [appPackageRows, setAppPackageRows] = useState<AppPackage[]>([]);
  const [effectiveFeatures, setEffectiveFeatures] = useState<string[]>([]);
  const [featureNameByCode, setFeatureNameByCode] = useState<
    Record<string, string>
  >({});
  const [appPackagesLoading, setAppPackagesLoading] = useState(false);
  const [appPackagesSaving, setAppPackagesSaving] = useState(false);

  const stats = useMemo(() => {
    const active = tenants.filter((t) => t.packageStatus === 'active').length;
    const pending = tenants.filter((t) => !t.setupCompleted).length;
    const expired = tenants.filter((t) => t.packageStatus === 'expired').length;
    return { total: tenants.length, active, pending, expired };
  }, [tenants]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (tenant) =>
        tenant.name.toLowerCase().includes(q) ||
        tenant.slug.toLowerCase().includes(q),
    );
  }, [tenants, search]);

  async function load() {
    setLoading(true);
    setListError(null);
    try {
      setTenants(await api.getTenants());
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function closeForm() {
    setFormOpen(false);
    setName('');
    setSlug('');
    setAdminName('');
    setAdminEmail('');
    const next = defaultPackageDates();
    setPackageStartsAt(next.start);
    setPackageEndsAt(next.end);
    setSlugTouched(false);
    setFormError(null);
  }

  function openCreate() {
    setMessage(null);
    setFormError(null);
    setSlugTouched(false);
    setInviteResult(null);
    const next = defaultPackageDates();
    setPackageStartsAt(next.start);
    setPackageEndsAt(next.end);
    setFormOpen(true);
  }

  function openEditPackage(tenant: Tenant) {
    setEditPackageTenant(tenant);
    setEditingRenewalId(null);
    const defaults = defaultPackageDates();
    setEditStart(toDateOnly(tenant.packageStartsAt) || defaults.start);
    setEditEnd(toDateOnly(tenant.packageEndsAt) || defaults.end);
    setFormError(null);
    setHistoryRows(tenant.packageRenewals ?? []);
    void (async () => {
      setHistoryLoading(true);
      try {
        setHistoryRows(await api.getTenantRenewals(tenant.id));
      } catch {
        /* keep cached rows */
      } finally {
        setHistoryLoading(false);
      }
    })();
  }

  function startEditRenewal(row: TenantPackageRenewal) {
    setEditingRenewalId(row.id);
    setEditStart(toDateOnly(row.startsAt));
    setEditEnd(toDateOnly(row.endsAt));
    setFormError(null);
  }

  async function savePackagePeriod(event: FormEvent) {
    event.preventDefault();
    if (!editPackageTenant) return;
    if (!editStart || !editEnd) {
      setFormError(labels.requiredFields);
      return;
    }
    setPackageSaving(true);
    setListError(null);
    try {
      let updated: Tenant;
      if (editingRenewalId) {
        updated = await api.updateTenantPackage(
          editPackageTenant.id,
          editingRenewalId,
          { packageStartsAt: editStart, packageEndsAt: editEnd },
        );
      } else if (historyRows.length === 0) {
        updated = await api.createTenantPackage(editPackageTenant.id, {
          packageStartsAt: editStart,
          packageEndsAt: editEnd,
        });
      } else {
        setFormError(labels.requiredFields);
        setPackageSaving(false);
        return;
      }
      setMessage(labels.packageSaved);
      setEditPackageTenant(updated);
      setHistoryRows(updated.packageRenewals ?? []);
      setEditingRenewalId(null);
      await load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setPackageSaving(false);
    }
  }

  async function renewPackage(tenant: Tenant) {
    const ok = await confirm({
      title: labels.renewConfirmTitle,
      message: labels.renewConfirmMessage,
      confirmText: labels.renewPackage,
      cancelText: t().common.cancel,
      confirmVariant: 'default',
    });
    if (!ok) return;

    setRenewingId(tenant.id);
    setListError(null);
    try {
      const updated = await api.renewTenantPackage(tenant.id);
      setMessage(labels.renewed);
      setEditPackageTenant(updated);
      setHistoryRows(updated.packageRenewals ?? []);
      setEditingRenewalId(null);
      await load();
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setRenewingId(null);
    }
  }

  async function deleteRenewal(renewal: TenantPackageRenewal) {
    if (!editPackageTenant) return;
    if (!isActivePackageRow(renewal)) return;

    const ok = await confirm({
      title: labels.deleteRenewalConfirmTitle,
      message: labels.deleteRenewalConfirmMessage,
      confirmText: labels.deleteRenewal,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;

    setDeletingRenewalId(renewal.id);
    setFormError(null);
    try {
      const updated = await api.deleteTenantRenewal(
        editPackageTenant.id,
        renewal.id,
      );
      setMessage(labels.renewalDeleted);
      setEditPackageTenant(updated);
      setEditStart(toDateOnly(updated.packageStartsAt) || editStart);
      setEditEnd(toDateOnly(updated.packageEndsAt) || editEnd);
      setHistoryRows(updated.packageRenewals ?? []);
      setEditingRenewalId(null);
      await load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setDeletingRenewalId(null);
    }
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!name.trim() || !slug.trim() || !adminName.trim() || !adminEmail.trim()) {
      setFormError(labels.requiredFields);
      return;
    }

    setSaving(true);
    try {
      const created = await api.createTenant({
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        admin: {
          name: adminName.trim(),
          email: adminEmail.trim(),
        },
        packageStartsAt,
        packageEndsAt,
      });
      setMessage(labels.created);
      closeForm();
      await load();
      if (created.inviteUrl) {
        setInviteResult({
          tenantId: created.id,
          inviteUrl: created.inviteUrl,
          email: created.admin?.email ?? adminEmail.trim(),
          tenantName: created.name,
          recipientName: created.admin?.name ?? adminName.trim(),
          inviteEmail: created.inviteEmail,
        });
      }
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  async function resendInvite(tenant: Tenant) {
    setResendingId(tenant.id);
    setListError(null);
    try {
      const result = await api.recreateTenantInvite(tenant.id);
      setInviteResult({
        tenantId: result.tenantId,
        inviteUrl: result.inviteUrl,
        email: result.admin.email,
        tenantName: tenant.name,
        recipientName: result.admin.name,
        inviteEmail: result.inviteEmail,
      });
      await load();
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setResendingId(null);
    }
  }

  async function openAppPackages(tenant: Tenant) {
    setAppPackagesTenant(tenant);
    setAppPackagesLoading(true);
    setFormError(null);
    try {
      const [result, features] = await Promise.all([
        api.getTenantAppPackages(tenant.id),
        api.getAppFeatures(),
      ]);
      setAppPackageRows(result.packages);
      setEffectiveFeatures(result.features);
      setFeatureNameByCode(
        Object.fromEntries(features.map((f) => [f.code, f.nameEl])),
      );
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t().common.error);
      setAppPackageRows([]);
      setEffectiveFeatures([]);
    } finally {
      setAppPackagesLoading(false);
    }
  }

  function toggleAppPackage(packageId: number) {
    setAppPackageRows((prev) =>
      prev.map((pkg) =>
        pkg.id === packageId ? { ...pkg, enabled: !pkg.enabled } : pkg,
      ),
    );
  }

  async function saveAppPackages() {
    if (!appPackagesTenant) return;
    setAppPackagesSaving(true);
    setFormError(null);
    try {
      const packageIds = appPackageRows
        .filter((pkg) => pkg.enabled)
        .map((pkg) => pkg.id);
      const result = await api.setTenantAppPackages(
        appPackagesTenant.id,
        packageIds,
      );
      setAppPackageRows(result.packages);
      setEffectiveFeatures(result.features);
      setMessage(labels.appPackagesSaved);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setAppPackagesSaving(false);
    }
  }

  async function enterTenant(tenant: Tenant) {
    setEnteringId(tenant.id);
    setListError(null);
    try {
      const result = await api.supportLogin(tenant.id);
      establishSession(result.accessToken, result.user);
      navigate('/');
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setEnteringId(null);
    }
  }

  async function toggleActive(tenant: Tenant) {
    try {
      await api.updateTenant(tenant.id, { active: !tenant.active });
      await load();
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  return (
    <PageShell>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <p className="text-sm text-muted">{labels.modalIntro}</p>
          <Button type="button" onClick={openCreate}>
            <PlusIcon className="mr-1.5 h-4 w-4" />
            {labels.newTenant}
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label={labels.statsTotal}
            value={stats.total}
            icon={<FolderIcon className="h-5 w-5" />}
          />
          <StatCard
            label={labels.statsActive}
            value={stats.active}
            tone="success"
            icon={<CheckCircleIcon className="h-5 w-5" />}
          />
          <StatCard
            label={labels.statsPending}
            value={stats.pending}
            tone="warning"
            icon={<ClockIcon className="h-5 w-5" />}
          />
          <StatCard
            label={labels.statsExpired}
            value={stats.expired}
            tone="warning"
            icon={<WarningIcon className="h-5 w-5" />}
          />
        </div>

        {message ? (
          <Callout variant="success">{message}</Callout>
        ) : null}
        {listError ? (
          <Callout variant="danger">{listError}</Callout>
        ) : null}

        <div className="max-w-md">
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={labels.searchPlaceholder}
            prefix={<SearchIcon className="h-4 w-4" />}
            showClearButton
          />
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-panel/60">
            <ListEmptyState
              title={tenants.length === 0 ? labels.emptyTitle : t().common.empty}
              hint={tenants.length === 0 ? labels.emptyHint : undefined}
              action={
                tenants.length === 0 ? (
                  <Button type="button" onClick={openCreate}>
                    <PlusIcon className="mr-1.5 h-4 w-4" />
                    {labels.newTenant}
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          <DataTablePanel className="min-h-[20rem]">
            <table className="min-w-full text-left text-sm">
              <thead className={dataTableHeadClass}>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                  <th className="px-4 py-3 font-semibold">{labels.colName}</th>
                  <th className="px-4 py-3 font-semibold">{labels.colPackage}</th>
                  <th className="px-4 py-3 font-semibold">{labels.colUsers}</th>
                  <th className="px-4 py-3 font-semibold">
                    {labels.colCustomers}
                  </th>
                  <th className="px-4 py-3 font-semibold">{labels.colSetup}</th>
                  <th className="px-4 py-3 font-semibold">{labels.colActive}</th>
                  <th className="px-4 py-3 font-semibold">{t().common.actions}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tenant) => {
                  const firstAdmin = tenant.users?.[0];
                  const invitePending = Boolean(firstAdmin?.mustChangePassword);
                  return (
                  <tr
                    key={tenant.id}
                    className="border-b border-line last:border-0 hover:bg-surface/70"
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-xs font-semibold text-teal-700 dark:text-teal-300">
                          {tenant.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-ink">
                            {tenant.name}
                          </p>
                          {firstAdmin?.email ? (
                            <p className="truncate text-xs text-muted">
                              {firstAdmin.email}
                            </p>
                          ) : tenant.email ? (
                            <p className="truncate text-xs text-muted">
                              {tenant.email}
                            </p>
                          ) : null}
                          <p className="truncate font-mono text-[11px] text-muted">
                            {tenant.slug}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="space-y-1">
                        <Badge variant={packageBadgeVariant(tenant.packageStatus)}>
                          {packageStatusLabel(tenant.packageStatus, labels)}
                        </Badge>
                        <p className="text-xs tabular-nums text-muted">
                          {formatDateGr(tenant.packageStartsAt)} –{' '}
                          {formatDateGr(tenant.packageEndsAt)}
                        </p>
                        {(tenant.renewalCount ?? 0) > 0 ? (
                          <p className="text-[11px] text-muted">
                            {labels.renewalsCount}: {tenant.renewalCount}
                          </p>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex items-center gap-1.5 tabular-nums">
                        <UsersIcon className="h-3.5 w-3.5 text-muted" />
                        {tenant._count?.users ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 tabular-nums">
                      {tenant._count?.customers ?? 0}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col gap-1">
                        {tenant.setupCompleted ? (
                          <Badge variant="success">{labels.setupDone}</Badge>
                        ) : (
                          <Badge variant="warning">
                            <span className="inline-flex items-center gap-1">
                              <WarningIcon className="h-3 w-3" />
                              {labels.setupPending}
                            </span>
                          </Badge>
                        )}
                        {invitePending ? (
                          <span className="text-[11px] text-amber-700 dark:text-amber-300">
                            {labels.invitePending}
                          </span>
                        ) : firstAdmin ? (
                          <span className="text-[11px] text-muted">
                            {labels.inviteAccepted}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <Switch
                          checked={tenant.active}
                          onChange={() => void toggleActive(tenant)}
                          aria-label={labels.colActive}
                        />
                        <span className="text-xs text-muted">
                          {tenant.active
                            ? labels.statusActive
                            : labels.statusInactive}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          title={labels.enterTenantTitle}
                          loading={enteringId === tenant.id}
                          onClick={() => void enterTenant(tenant)}
                        >
                          <ExternalLinkIcon className="mr-1 h-3.5 w-3.5" />
                          {labels.enterTenant}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => openEditPackage(tenant)}
                        >
                          <PencilIcon className="mr-1 h-3.5 w-3.5" />
                          {labels.managePackage}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => void openAppPackages(tenant)}
                        >
                          <FolderIcon className="mr-1 h-3.5 w-3.5" />
                          {labels.manageAppPackages}
                        </Button>
                        {invitePending ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            loading={resendingId === tenant.id}
                            onClick={() => void resendInvite(tenant)}
                          >
                            <RefreshIcon className="h-3.5 w-3.5" />
                            {labels.resendInvite}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </DataTablePanel>
        )}

      <Modal isOpen={formOpen} onClose={closeForm} size="lg">
        <form onSubmit={handleCreate} className="flex min-h-0 flex-1 flex-col">
          <ModalHeader>
            <ModalTitle>{labels.newTenant}</ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={closeForm}
              aria-label={t().common.cancel}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-5">
            <p className="text-sm text-muted">{labels.modalIntro}</p>
            {formError ? (
              <Callout variant="danger">{formError}</Callout>
            ) : null}

            <section className="space-y-3 rounded-lg border border-line bg-surface/40 p-4">
              <h3 className="text-sm font-semibold text-ink">
                {labels.companyName}
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="tenant-name">{labels.companyName}</Label>
                  <TextInput
                    id="tenant-name"
                    value={name}
                    onChange={(e) => {
                      const next = e.target.value;
                      setName(next);
                      if (!slugTouched) {
                        setSlug(slugify(next));
                      }
                    }}
                    autoFocus
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="tenant-slug">{labels.slug}</Label>
                  <TextInput
                    id="tenant-slug"
                    value={slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlug(e.target.value);
                    }}
                    placeholder="acme"
                  />
                  <p className="mt-1 text-xs text-muted">{labels.slugHint}</p>
                </div>
              </div>
            </section>

            <section className="space-y-3 rounded-lg border border-line bg-surface/40 p-4">
              <div>
                <h3 className="text-sm font-semibold text-ink">
                  {labels.colPackage}
                </h3>
                <p className="mt-0.5 text-xs text-muted">{labels.packageHint}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="pkg-start">{labels.packageFrom}</Label>
                  <DateInput
                    id="pkg-start"
                    value={packageStartsAt}
                    onChange={(e) => setPackageStartsAt(e.target.value)}
                    showClearButton={false}
                  />
                </div>
                <div>
                  <Label htmlFor="pkg-end">{labels.packageTo}</Label>
                  <DateInput
                    id="pkg-end"
                    value={packageEndsAt}
                    onChange={(e) => setPackageEndsAt(e.target.value)}
                    showClearButton={false}
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3 rounded-lg border border-line bg-surface/40 p-4">
              <div>
                <h3 className="text-sm font-semibold text-ink">
                  {labels.firstAdmin}
                </h3>
                <p className="mt-0.5 text-xs text-muted">
                  {labels.firstAdminHint}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="admin-name">{labels.adminName}</Label>
                  <TextInput
                    id="admin-name"
                    value={adminName}
                    onChange={(e) => setAdminName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="admin-email">{labels.adminEmail}</Label>
                  <TextInput
                    id="admin-email"
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                  />
                </div>
              </div>
            </section>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={closeForm}>
              <XIcon className="h-4 w-4" />
              {t().common.cancel}
            </Button>
            <Button type="submit" loading={saving}>
              <PlusIcon className="h-4 w-4" />
              {labels.newTenant}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(editPackageTenant)}
        onClose={() => {
          setEditPackageTenant(null);
          setEditingRenewalId(null);
        }}
        size="lg"
      >
        <form
          onSubmit={savePackagePeriod}
          className="flex min-h-0 flex-1 flex-col"
        >
          <ModalHeader>
            <ModalTitle>{labels.editPackage}</ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditPackageTenant(null);
                setEditingRenewalId(null);
              }}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-ink">
                {editPackageTenant?.name}
              </p>
              {editPackageTenant ? (
                <Badge
                  variant={packageBadgeVariant(editPackageTenant.packageStatus)}
                >
                  {packageStatusLabel(editPackageTenant.packageStatus, labels)}
                </Badge>
              ) : null}
            </div>
            {formError && editPackageTenant ? (
              <Callout variant="danger">{formError}</Callout>
            ) : null}

            {historyLoading ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner />
              </div>
            ) : historyRows.length === 0 || editingRenewalId !== null ? (
              <div className="space-y-3">
                <p className="text-sm text-muted">
                  {historyRows.length === 0
                    ? labels.noRenewals
                    : labels.editRenewal}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="edit-pkg-start">{labels.packageFrom}</Label>
                    <DateInput
                      id="edit-pkg-start"
                      value={editStart}
                      onChange={(e) => setEditStart(e.target.value)}
                      showClearButton={false}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-pkg-end">{labels.packageTo}</Label>
                    <DateInput
                      id="edit-pkg-end"
                      value={editEnd}
                      onChange={(e) => setEditEnd(e.target.value)}
                      showClearButton={false}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {historyRows.length > 0 ? (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-ink">
                  {labels.renewalsTitle}
                </h3>
                <div className="overflow-hidden rounded-lg border border-line">
                  <table className="min-w-full text-left text-sm">
                    <thead className={dataTableHeadClass}>
                      <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                        <th className="px-3 py-2 font-semibold">
                          {labels.packageFrom}
                        </th>
                        <th className="px-3 py-2 font-semibold">
                          {labels.packageTo}
                        </th>
                        <th className="px-3 py-2 font-semibold">
                          {labels.renewalKindCol}
                        </th>
                        <th className="px-3 py-2 font-semibold text-right">
                          {t().common.actions}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyRows.map((row) => {
                        const active = isActivePackageRow(row);
                        return (
                          <tr
                            key={row.id}
                            className="border-b border-line last:border-0"
                          >
                            <td className="px-3 py-2 tabular-nums">
                              {formatDateGr(row.startsAt)}
                            </td>
                            <td className="px-3 py-2 tabular-nums">
                              {formatDateGr(row.endsAt)}
                            </td>
                            <td className="px-3 py-2">
                              <Badge
                                variant={
                                  row.kind === 'renewal'
                                    ? 'success'
                                    : 'secondary'
                                }
                              >
                                {renewalKindLabel(row.kind, labels)}
                              </Badge>
                            </td>
                            <td className="px-3 py-2">
                              {active ? (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => startEditRenewal(row)}
                                  >
                                    <PencilIcon className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    loading={deletingRenewalId === row.id}
                                    onClick={() => void deleteRenewal(row)}
                                    aria-label={labels.deleteRenewal}
                                  >
                                    <TrashIcon className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </ModalContent>
          <ModalFooter>
            {historyRows.length > 0 ? (
              <div className="mr-auto">
                <Button
                  type="button"
                  variant="outline"
                  loading={
                    editPackageTenant
                      ? renewingId === editPackageTenant.id
                      : false
                  }
                  onClick={() =>
                    editPackageTenant && void renewPackage(editPackageTenant)
                  }
                >
                  <RefreshIcon className="h-4 w-4" />
                  {labels.renewPackage}
                </Button>
              </div>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (editingRenewalId) {
                  setEditingRenewalId(null);
                  return;
                }
                setEditPackageTenant(null);
              }}
            >
              <XIcon className="h-4 w-4" />
              {t().common.cancel}
            </Button>
            {historyRows.length === 0 || editingRenewalId !== null ? (
              <Button type="submit" loading={packageSaving}>
                {historyRows.length === 0 ? (
                  <PlusIcon className="h-4 w-4" />
                ) : (
                  <SaveIcon className="h-4 w-4" />
                )}
                {historyRows.length === 0
                  ? labels.createPackage
                  : t().common.save}
              </Button>
            ) : null}
          </ModalFooter>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(appPackagesTenant)}
        onClose={() => setAppPackagesTenant(null)}
        size="lg"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <ModalHeader>
            <ModalTitle>{labels.manageAppPackagesTitle}</ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setAppPackagesTenant(null)}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-5">
            <div>
              <p className="text-sm font-medium text-ink">
                {appPackagesTenant?.name}
              </p>
              <p className="mt-1 text-sm text-muted">{labels.appPackagesHint}</p>
            </div>
            {formError && appPackagesTenant ? (
              <Callout variant="danger">{formError}</Callout>
            ) : null}
            {appPackagesLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : appPackageRows.length === 0 ? (
              <p className="text-sm text-muted">{labels.noAppPackages}</p>
            ) : (
              <div className="space-y-2">
                {appPackageRows.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="flex items-start justify-between gap-3 rounded-lg border border-line px-3 py-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{pkg.name}</p>
                      {pkg.description ? (
                        <p className="text-xs text-muted">{pkg.description}</p>
                      ) : null}
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {pkg.features.map((f) => (
                          <Badge key={f.id} variant="secondary">
                            {f.nameEl}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Switch
                      checked={Boolean(pkg.enabled)}
                      onChange={() => toggleAppPackage(pkg.id)}
                      aria-label={pkg.name}
                    />
                  </div>
                ))}
              </div>
            )}
            {effectiveFeatures.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-ink">
                  {labels.effectiveFeatures}
                </p>
                <div className="flex flex-wrap gap-1">
                  {effectiveFeatures.map((code) => (
                    <Badge key={code} variant="success">
                      {featureNameByCode[code] ?? code}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
          </ModalContent>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAppPackagesTenant(null)}
            >
              {t().common.cancel}
            </Button>
            <Button
              type="button"
              loading={appPackagesSaving}
              onClick={() => void saveAppPackages()}
            >
              <SaveIcon className="mr-1 h-4 w-4" />
              {t().common.save}
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      <TenantInviteReadyModal
        invite={inviteResult}
        onClose={() => setInviteResult(null)}
      />
    </PageShell>
  );
}
